import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../api';

const AuthContext = createContext();

// Helper functions for role permissions API
const getRoles = async () => {
  try {
    const response = await api.get('/permissions/roles');
    return response.data;
  } catch (error) {
    console.error('Error fetching roles:', error);
    return [];
  }
};

const getRolePermissions = async (roleId) => {
  try {
    const response = await api.get(`/permissions/roles/${roleId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching role permissions:', error);
    return {};
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [capabilities, setCapabilities] = useState({});
  const [capabilitiesLoading, setCapabilitiesLoading] = useState(false);

  // Check if token is expired
  const isTokenExpired = (token) => {
    if (!token) return true;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp * 1000 < Date.now();
    } catch (error) {
      return true;
    }
  };

  // Initialize auth state from localStorage
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          const userData = JSON.parse(storedUser);
          
          // Check if token is still valid
          if (userData.token && !isTokenExpired(userData.token)) {
            setUser(userData);
            // Set token in axios headers
            api.defaults.headers.common['Authorization'] = `Bearer ${userData.token}`;
            
            // Fetch capabilities after setting user
            await fetchCapabilities(userData);
          } else {
            // Token expired, clear storage
            localStorage.removeItem('user');
            setUser(null);
            delete api.defaults.headers.common['Authorization'];
          }
        }
      } catch (error) {
        localStorage.removeItem('user');
        setUser(null);
        delete api.defaults.headers.common['Authorization'];
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Auto logout when token expires
  useEffect(() => {
    if (!user) return;

    const checkTokenExpiration = () => {
      if (isTokenExpired(user.token)) {
        logout();
      }
    };

    // Check every minute
    const interval = setInterval(checkTokenExpiration, 60000);
    return () => clearInterval(interval);
  }, [user]);

  const login = async (userData) => {
    try {
      // Validate userData structure
      if (!userData || !userData.token) {
        throw new Error('Invalid user data structure');
      }
      
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      // Set token in axios headers
      api.defaults.headers.common['Authorization'] = `Bearer ${userData.token}`;
      
      // Fetch capabilities after login
      await fetchCapabilities(userData);
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    try {
      setUser(null);
      setCapabilities({});
      localStorage.removeItem('user');
      // Clear token from axios headers
      delete api.defaults.headers.common['Authorization'];
    } catch (error) {
      // Silent error handling
    }
  };

  const updateUser = (updates) => {
    try {
      const updatedUser = { ...user, ...updates };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    } catch (error) {
      // Silent error handling
    }
  };

  const isAuthenticated = () => {
    try {
      const authenticated = user && user.token && !isTokenExpired(user.token);
      return authenticated;
    } catch (error) {
      return false;
    }
  };

  const hasRole = (role) => {
    try {
      if (!user || !user.roles || !Array.isArray(user.roles)) {
        return false;
      }
      
      // Check if roles array contains the role (handle both string and object formats)
      const hasRoleResult = user.roles.some(r => {
        // If r is a string, compare directly
        if (typeof r === 'string') {
          return r === role;
        }
        // If r is an object with 'name' property, compare the name
        if (typeof r === 'object' && r.name) {
          return r.name === role;
        }
        return false;
      });
      
      return hasRoleResult;
    } catch (error) {
      return false;
    }
  };

  // Fetch user capabilities from role permissions API
  const fetchCapabilities = async (userData) => {
    try {
      setCapabilitiesLoading(true);
      
      // Get all roles mapping (id <-> name)
      const allRoles = await getRoles().catch(() => []);
      
      // Normalize role name variants from auth payload
      const userRoleNames = Array.isArray(userData?.roles) 
        ? userData.roles.map(r => typeof r === 'object' ? r.name : r)
        : [];
      
      // Map user roles (e.g., 'ROLE_ADMIN' or 'ADMIN') to role ids
      const myRoleIds = allRoles
        .filter(r => {
          const nameUpper = (r.name || r.nameRole || '').toString().toUpperCase();
          return userRoleNames.some(ur => {
            const urUpper = ur.toString().toUpperCase();
            return urUpper === `ROLE_${nameUpper}` || urUpper === nameUpper;
          });
        })
        .map(r => r.id);

      // Fetch permissions for each role and merge (any true wins)
      const permsList = await Promise.all(
        (myRoleIds || []).map(rid => getRolePermissions(rid).catch(() => ({})))
      );

      const merged = {};
      for (const rp of permsList) {
        Object.entries(rp || {}).forEach(([moduleKey, caps]) => {
          merged[moduleKey] = merged[moduleKey] || {};
          Object.entries(caps || {}).forEach(([capKey, val]) => {
            merged[moduleKey][capKey] = Boolean(merged[moduleKey][capKey]) || Boolean(val);
          });
        });
      }


      setCapabilities(merged);
      return merged;
    } catch (e) {
      console.error('Error loading capabilities:', e);
      setCapabilities({});
      return {};
    } finally {
      setCapabilitiesLoading(false);
    }
  };

  // Check if user has a specific capability in a module.
  // Admin and director roles always have full access (mirrors ModuleGuard behaviour).
  const can = (capabilityKey, moduleKey = 'proposal') => {
    try {
      if (
        hasRole('ROLE_ADMIN') ||
        hasRole('ROLE_DIRECTOR') ||
        hasRole('admin') ||
        hasRole('director')
      ) {
        return true;
      }
      const mod = capabilities[moduleKey] || {};
      return !!mod[capabilityKey];
    } catch (error) {
      return false;
    }
  };

  const value = {
    user,
    loading,
    login,
    logout,
    updateUser,
    isAuthenticated,
    hasRole,
    can,
    capabilities,
    capabilitiesLoading,
    fetchCapabilities
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
